import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import Cookies from 'js-cookie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, faCrown, faMoneyBillWave, 
  faCoins, faTicketAlt, faCalendarCheck 
} from '@fortawesome/free-solid-svg-icons';
import UserCenterActions from '../../components/UserCenter/UserCenterActions';
import '../../styles/UserCenter.css';
import imgPrefix from '../../imgConfig'; // 导入 imgConfig.js


const isBrowser = typeof window !== 'undefined';
const isPages = isBrowser && /pages\.dev$/.test(window.location.hostname);
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || (isPages ? 'https://api-do.whitelotus.work' : ''));

const UserCenter = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 获取用户信息并可能重定向到用户详情页
    let storedUser = localStorage.getItem('userData');
    
    // 如果userData不存在，尝试使用user键
    if (!storedUser) {
      storedUser = localStorage.getItem('user');
      // 如果找到了user信息，同时存储到userData中以保持一致性
      if (storedUser) {
        localStorage.setItem('userData', storedUser);
      }
    }
    
    if (!storedUser) {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);
      setUserData(parsedUser);
      
      // 检查是否应该重定向到用户详情页
      const pathParts = window.location.pathname.split('/');
      if (pathParts[1] === 'user-center' && parsedUser.id) {
        // 如果当前是在用户中心页面，则重定向到用户详情页
        navigate(`/users/${parsedUser.id}`);
        return;
      }
      
      fetchAppointments(parsedUser.id);
    } catch (err) {
      console.error('Error parsing user data', err);
      navigate('/login');
    }
  }, [navigate]);

  const fetchAppointments = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/jiaolianyuyue/list?userid=${userId}&limit=5`, {
        headers: {
          'token': Cookies.get('token')
        }
      });
      
      if (response.data.code === 0) {
        setAppointments(response.data.data.list || []);
      } else {
        setError(response.data.msg || '获取预约列表失败');
      }
    } catch (err) {
      console.error('Error fetching appointments', err);
      setError('网络错误，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '未设置';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusDisplay = (status) => {
    switch(status) {
      case '已支付':
        return <span className="status-paid">已支付</span>;
      case '已完成':
        return <span className="status-completed">已完成</span>;
      case '已取消':
        return <span className="status-canceled">已取消</span>;
      case '申请退款':
        return <span className="status-pending">退款申请中</span>;
      case '已退款':
        return <span className="status-refunded">已退款</span>;
      case '退款失败':
        return <span className="status-failed">退款失败</span>;
      case '审核中':
        return <span className="status-pending">审核中</span>;
      default:
        return <span className="status-pending">待确认</span>;
    }
  };

  if (!userData) {
    return (
      <div className="loading-message">加载中...</div>
    );
  }

  return (
    <div className="user-center-container">
      <div className="user-center-header">
        <div className="user-avatar">
          <img 
            src={userData.touxiang || `${imgPrefix}/upload/default-avatar.png`}
            alt="用户头像"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `${imgPrefix}/upload/default-avatar.png`;
            }}
          />

        </div>
        <div className="user-info">
          <h1>{userData.name || userData.account || '用户'}</h1>
          <div className="user-tags">
            <span className="user-tag">ID: {userData.id}</span>
            {userData.vipFlag && (
              <span className="vip-tag">
                <FontAwesomeIcon icon={faCrown} /> VIP会员
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="account-stats">
        <div className="stat-card stat-card-balance">
          <div className="stat-title">
            <FontAwesomeIcon icon={faMoneyBillWave} />
            账户余额
          </div>
          <div className="stat-value">¥{userData.money || '0.00'}</div>
          <div className="stat-actions">
            <button className="stat-link" onClick={() => navigate('/recharge')}>
              充值
            </button>
          </div>
        </div>
        
        <div className="stat-card stat-card-points">
          <div className="stat-title">
            <FontAwesomeIcon icon={faCoins} />
            积分
          </div>
          <div className="stat-value">{userData.jifen || 0}</div>
          <div className="stat-actions">
            <button className="stat-link">
              兑换
            </button>
          </div>
        </div>
        
        <div className="stat-card stat-card-coupons">
          <div className="stat-title">
            <FontAwesomeIcon icon={faTicketAlt} />
            优惠券
          </div>
          <div className="stat-value">{userData.coupons || 0}</div>
          <div className="stat-actions">
            <button className="stat-link">
              查看
            </button>
          </div>
        </div>
      </div>

      <UserCenterActions />

      <div className="appointment-section">
        <h2 className="section-title">
          <FontAwesomeIcon icon={faCalendarCheck} />
          近期预约
        </h2>
        
        {loading ? (
          <div className="loading-message">加载中...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : appointments.length === 0 ? (
          <div className="no-data-message">暂无预约记录</div>
        ) : (
          <div className="appointment-list">
            {appointments.map(appointment => (
              <div className="appointment-item" key={appointment.id}>
                <div className="appointment-info">
                  <div className="appointment-id">预约编号: {appointment.id}</div>
                  <div className="appointment-coach">教练: {appointment.jiaolianxingming}</div>
                  <div className="appointment-time">
                    时间: {formatDate(appointment.yuyueshijian)}
                  </div>
                </div>
                <div className="appointment-status">
                  {getStatusDisplay(appointment.yuyuezhuangtai)}
                </div>
              </div>
            ))}
            
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/appointments')}
            >
              查看全部预约
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserCenter;
